import Image from 'next/image';
import { Dispatch, HTMLAttributes, RefObject, SetStateAction, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../../../design-system/components/form/button/Button';
import { IconArrowRight } from '../../../../../../design-system/components/icons/ArrowRight';
import { NavItemButton } from '../../../../../../design-system/components/nav/nav-item/NavItem';
import { imageLoaderForPath } from '../../../../../../domains/Layout/Image/URL/ImageURL';
import { NormalizedWhereToGoMenu } from '../../../../../../resources/whereToGoMenu/api/prismic/normalizers';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { Container } from '../../../../Container/Container';
import { MobileMenuSearchInput } from '../../../Search/MobileMenuSearchInput';
import { PrimaryNavThematicCategoryValue, PrimaryNavValue } from '../../../types';
import { MobileMenuList } from '../../MenuList/MobileMenuList';
import { MobileSubNav } from '../MobileSubNav/MobileSubNav';
import { MobileView } from '../MobileView';

import { CampaignView } from './CampaignView';
import { HowLongView } from './HowLongView';
import style from './NewWhereToGoMobileView.module.css';
import { WhatView } from './WhatView';
import { WhenView } from './WhenView';
import { WhoView } from './WhoView';

export function NewWhereToGoMobileView({
    whereToGoMenu,
    setNavValue,
    onOpenSearchOverlay,
    searchTriggerRef,
}: {
    whereToGoMenu: NonNullable<NormalizedWhereToGoMenu>;
    setNavValue: Dispatch<SetStateAction<PrimaryNavValue>>;
    onOpenSearchOverlay: () => void;
    searchTriggerRef: RefObject<HTMLButtonElement | null>;
}) {
    const { formatMessage } = useIntl();
    const [currentThematicCategory, setCurrentThematicCategory] =
        useState<PrimaryNavThematicCategoryValue>(null);
    const { trackEvent } = useTrackEventAmplitude();

    const whereToGoTitle = currentThematicCategory
        ? null
        : formatMessage({
              id: 'global.header.globalNav.new.whereToGo.label',
          });

    return (
        <>
            <MobileSubNav
                showPreviousButton={currentThematicCategory !== null ? true : false}
                onPrevious={() => {
                    setNavValue('WHERE_TO_GO');
                    setCurrentThematicCategory(null);
                    trackEvent((ampli, defaultProperties) => {
                        ampli.primaryNavPreviousStepClicked({
                            ...defaultProperties,
                            block: 'primary_nav',
                        });
                    });
                }}
                setNavValue={setNavValue}
            />
            <MobileView title={whereToGoTitle}>
                <Container>
                    {currentThematicCategory === null ? (
                        <div className={style.searchInputWrapper}>
                            <MobileMenuSearchInput
                                ref={searchTriggerRef}
                                onClick={onOpenSearchOverlay}
                            />
                        </div>
                    ) : null}

                    {currentThematicCategory === null ? (
                        <WhereToGoNav
                            whereToGoMenu={whereToGoMenu}
                            onThematicCategoryChange={(thematic) => {
                                setCurrentThematicCategory(thematic);
                            }}
                        />
                    ) : null}

                    {whereToGoMenu.campaign && currentThematicCategory === 'campaign' ? (
                        <CampaignView
                            title={whereToGoMenu.campaign.title}
                            campaign={whereToGoMenu.campaign}
                        />
                    ) : null}

                    {whereToGoMenu.who && currentThematicCategory === 'who' ? (
                        <WhoView title={whereToGoMenu.who.title} who={whereToGoMenu.who} />
                    ) : null}

                    {whereToGoMenu.what && currentThematicCategory === 'what' ? (
                        <WhatView what={whereToGoMenu.what} title={whereToGoMenu.what.title} />
                    ) : null}

                    {whereToGoMenu.when && currentThematicCategory === 'when' ? (
                        <WhenView when={whereToGoMenu.when} title={whereToGoMenu.when.title} />
                    ) : null}

                    {whereToGoMenu.howLong && currentThematicCategory === 'howLong' ? (
                        <HowLongView
                            title={whereToGoMenu.howLong.title}
                            howLong={whereToGoMenu.howLong}
                        />
                    ) : null}
                </Container>
            </MobileView>
        </>
    );
}

type WhereToGoNavProps = HTMLAttributes<HTMLElement> & {
    whereToGoMenu: NonNullable<NormalizedWhereToGoMenu>;
    onThematicCategoryChange: (thematic: PrimaryNavThematicCategoryValue) => void;
};

const CampaignIcon = () => (
    <Image
        src="/separators/star-yellow.png"
        alt=""
        width={20}
        height={16}
        loader={imageLoaderForPath}
        className={style.campaignIcon}
    />
);

function WhereToGoNav({ whereToGoMenu, onThematicCategoryChange, ...props }: WhereToGoNavProps) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const sections = ['campaign', 'who', 'when', 'what', 'howLong'] as const;
    const rowNumbers = sections.reduce(
        (acc, section) => {
            if (whereToGoMenu[section]) {
                acc[section] = Object.keys(acc).length + 1;
            }
            return acc;
        },
        {} as Record<string, number>,
    );

    return (
        <>
            <MobileMenuList {...props}>
                {whereToGoMenu.campaign ? (
                    <NavItemButton
                        iconTrailing={<IconArrowRight />}
                        onClick={() => {
                            onThematicCategoryChange('campaign');
                            trackEvent((ampli, defaultProperties) => {
                                ampli.primaryNavThematicSelected({
                                    ...defaultProperties,
                                    block: 'primary_nav',
                                    row_number: rowNumbers.campaign,
                                    thematic_group: 'campaign',
                                });
                            });
                        }}
                    >
                        <span className={style.campaignLabel}>
                            {whereToGoMenu.campaign.title}
                            <CampaignIcon />
                        </span>
                    </NavItemButton>
                ) : null}

                {whereToGoMenu.who ? (
                    <NavItemButton
                        iconTrailing={<IconArrowRight />}
                        onClick={() => {
                            onThematicCategoryChange('who');
                            trackEvent((ampli, defaultProperties) => {
                                ampli.primaryNavThematicSelected({
                                    ...defaultProperties,
                                    block: 'primary_nav',
                                    row_number: rowNumbers.who,
                                    thematic_group: 'who',
                                });
                            });
                        }}
                    >
                        {whereToGoMenu.who.title}
                    </NavItemButton>
                ) : null}

                {whereToGoMenu.when ? (
                    <NavItemButton
                        iconTrailing={<IconArrowRight />}
                        onClick={() => {
                            onThematicCategoryChange('when');
                            trackEvent((ampli, defaultProperties) => {
                                ampli.primaryNavThematicSelected({
                                    ...defaultProperties,
                                    block: 'primary_nav',
                                    row_number: rowNumbers.when,
                                    thematic_group: 'when',
                                });
                            });
                        }}
                    >
                        {whereToGoMenu.when.title}
                    </NavItemButton>
                ) : null}

                {whereToGoMenu.what ? (
                    <NavItemButton
                        iconTrailing={<IconArrowRight />}
                        onClick={() => {
                            onThematicCategoryChange('what');
                            trackEvent((ampli, defaultProperties) => {
                                ampli.primaryNavThematicSelected({
                                    ...defaultProperties,
                                    block: 'primary_nav',
                                    row_number: rowNumbers.what,
                                    thematic_group: 'what',
                                });
                            });
                        }}
                    >
                        {whereToGoMenu.what.title}
                    </NavItemButton>
                ) : null}

                {whereToGoMenu.howLong ? (
                    <NavItemButton
                        iconTrailing={<IconArrowRight />}
                        onClick={() => {
                            onThematicCategoryChange('howLong');
                            trackEvent((ampli, defaultProperties) => {
                                ampli.primaryNavThematicSelected({
                                    ...defaultProperties,
                                    block: 'primary_nav',
                                    row_number: rowNumbers.howLong,
                                    thematic_group: 'how_long',
                                });
                            });
                        }}
                    >
                        {whereToGoMenu.howLong.title}
                    </NavItemButton>
                ) : null}
            </MobileMenuList>

            <div className={style.footer}>
                <Button
                    priority="secondary"
                    reversed
                    href={generatePath('where_to_go')}
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.primaryNavWheretogoClicked({
                                ...defaultProperties,
                                block: 'primary_nav',
                            });
                        });
                    }}
                >
                    {formatMessage({
                        id: 'global.header.globalNav.new.whereToGo.cta',
                    })}
                </Button>
            </div>
        </>
    );
}
